import React from "react";
import { motion } from "framer-motion";
import { FaBriefcase, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";

// Import company logos
import googLogo from "../../assets/images/googLogo.webp";
import hoodLogo from "../../assets/images/hoodLogo.jpeg";
import pingLogo from "../../assets/images/ping.png";
import forgerockLogo from "../../assets/images/forgerock.png";
import aristaLogo from "../../assets/images/arista.png";
import ubloodLogo from "../../assets/images/ublood.png";
import kbsLogo from "../../assets/images/kbs.png";
import primatechLogo from "../../assets/images/primatech.png";

const experiences = [
  {
    id: 1,
    title: "Security Engineering",
    company: "Google",
    location: "Sunnyvale, CA",
    duration: "2026 - Present",
    description: [
      "Google Cloud Platform - Office of CISO",
      "Application & Cloud Security, Bug Bounty (Vulnerability Rewards Program)",
    ],
    technologies: [
      "Application Security",
      "Cloud Security",
      "Bug Bounty",
    ],
    logo: googLogo,
  },
  {
    id: 2,
    title: "Staff Security Engineer, Technical Lead",
    company: "Robinhood",
    location: "Bellevue, WA",
    duration: "2025-2026",
    description: [
      "security initiatives across Cloud Security, Application Security, and Privacy functions",
      "Improving compliance workflows to exceed regulatory requirements, streamlining audit readiness",
      "Enhancing cloud and application security practices through cross-team collaboration and process automation",
    ],
    technologies: [
      "Application Security",
      "Cloud Security",
      "Security Assessments",
    ],
    logo: hoodLogo,
  },
  {
    id: 3,
    title: "Senior Staff Application Security Engineer",
    company: "Ping Identity",
    location: "Denver, CO",
    duration: "2023 - 2025",
    description: [
      "Led security efforts for Ping ID Marketplace anti abuse product security",
      "Conducted security assessments for third party OEM integration software, identifying a critical RCE vulnerability",
      "Performed cloud security audits and operational security cost cuts",
    ],
    technologies: [
      "Application Security",
      "Cloud Security",
      "Security Assessments",
    ],
    logo: pingLogo,
  },
  {
    id: 4,
    title: "Senior Staff Application Security Engineer",
    company: "ForgeRock (now Ping Identity)",
    location: "San Francisco, CA",
    duration: "2021 - 2023",
    description: [
      "Architected the security model for a cloud-based Identity & Access Management service hosted using Kubernetes on the Google Cloud Platform",
      "Saved the company over $500,000 in tangible security expenses through automation, security training and penetration testing efforts",
      "Supervised and validated Product Bug Bounty programs at ForgeRock",
    ],
    technologies: ["IAM", "Kubernetes", "GCP", "Bug Bounty"],
    logo: forgerockLogo,
  },
  {
    id: 5,
    title: "Senior Cloud Security Engineer",
    company: "Arista NDR",
    location: "Santa Clara, CA",
    duration: "2019 - 2021",
    description: [
      "Filed a patent for automated defense in cloud-native environments, contributing to company acquisition",
      "Led a team of 4 engineers in product development and commercialization of a cloud security product",
      "Improved the Kubernetes and AWS Security solutions by collaborating with the security research team",
    ],
    technologies: ["Cloud Security", "Kubernetes", "AWS", "Patents"],
    logo: aristaLogo,
  },
  {
    id: 6,
    title: "Security Software Engineer",
    company: "UBlood",
    location: "Remote",
    duration: "2020",
    description: [
      "Performed a security assessment before product launch and developed a secure SDLC pipeline",
      "Identified and mitigated multiple security concerns for Android, iOS, and AWS applications",
    ],
    technologies: ["Mobile Security", "AWS", "SDLC", "Security Assessment"],
    logo: ubloodLogo,
  },
  {
    id: 7,
    title: "Application Security Engineer",
    company: "KBS Ltd.",
    location: "Mumbai, India",
    duration: "2016 - 2019",
    description: [
      "Implemented MFA authentication and secure token services for applications",
      "Developed automation scripts for third-party dependency upgrades, improving efficiency by over 65%",
    ],
    technologies: ["MFA", "Security Automation", "Application Security"],
    logo: kbsLogo,
  },
  {
    id: 8,
    title: "Software Engineer",
    company: "PrimaTech Solutions",
    location: "Mumbai, India",
    duration: "2014 - 2016",
    description: [
      "Developed a backend infrastructure for an investment Banking startup, deployed it on Google Kubernetes Engine, and used GCP managed services also designed an android and iOS app as Frontend solutions",
      "Implemented a microservice architecture for a web app from a monolithic architecture, revising the AWS pricing factor to half of the original price and creating a 30% more performant code",
    ],
    technologies: ["GKE", "Microservices", "Mobile Development", "AWS"],
    logo: primatechLogo,
  },
];

const ProfessionalExperience = () => {
  return (
    <section className="py-12">
      <div className="container mx-auto px-4">
        <motion.h2
          className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Professional Experience
        </motion.h2>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-400 via-purple-400 to-transparent hidden md:block" />

          {experiences.map((exp, index) => (
            <motion.div
              key={exp.id}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="relative mb-12"
            >
              {/* Timeline dot */}
              <div className="absolute left-6 w-4 h-4 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full hidden md:block" />

              <div className="ml-0 md:ml-16">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-300 group"
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    {/* Company Logo */}
                    {exp.logo && (
                      <div className="w-16 h-16 bg-white/10 rounded-lg p-2 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                        <img
                          src={exp.logo}
                          alt={exp.company}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}

                    <div className="flex-1">
                      {/* Header */}
                      <div className="mb-4">
                        <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                          {exp.title}
                        </h3>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                          <div className="flex items-center gap-2">
                            <FaBriefcase className="text-blue-400" />
                            <span>{exp.company}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaMapMarkerAlt className="text-purple-400" />
                            <span>{exp.location}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaCalendarAlt className="text-green-400" />
                            <span>{exp.duration}</span>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <ul className="mb-4 space-y-2">
                        {exp.description.map((item, idx) => (
                          <li
                            key={idx}
                            className="text-gray-300 flex items-start"
                          >
                            <span className="text-blue-400 mr-2">▸</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Technologies */}
                      <div className="flex flex-wrap gap-2">
                        {exp.technologies.map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 rounded-full text-sm border border-blue-400/30 group-hover:border-blue-400/50 transition-colors"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProfessionalExperience;
